import React, { useEffect, useMemo } from "react";

import { Target } from "lucide-react";

import SynopsisSectionCard from "./SynopsisSectionCard";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import type {
  ExecutiveTeamMember,
  StakeholderRelationship,
} from "../../../Store/Reducers/AccountDetailsSlice";
import { fetchRlSettingConfig } from "../../../Store/Reducers/VendorAdminSlice";
import {
  DEFAULT_FAN_IN_CAP,
  normalizeFanInCap,
} from "../../../Utils/Helper/rlFanCaps";

type CoverageStat = {
  id: string;
  value: string;
  label: string;
  subText?: string;
  valueClassName?: string;
  tooltip?: string;
};

type AlignmentCoverageSectionProps = {
  assessmentLevel?: string | number | null;
  executiveSponsors: ExecutiveTeamMember[];
  relationships: StakeholderRelationship[];
  loading: boolean;
  onOpenRelationships: () => void;
  /** When true, renders header + body without the outer card chrome. */
  embedded?: boolean;
};

const parseAlNumber = (
  value: string | number | null | undefined
): number | null => {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const match = String(value).match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
};

const hasGoal = (
  assessment: StakeholderRelationship["latest_assessment"]
): boolean => {
  if (!assessment) return false;
  const extra = assessment as StakeholderRelationship["latest_assessment"] & {
    target_rl_score?: number | null;
  };
  if (
    assessment.target_rl != null &&
    Number.isFinite(Number(assessment.target_rl))
  ) {
    return true;
  }
  if (
    extra.target_rl_score != null &&
    Number.isFinite(Number(extra.target_rl_score))
  ) {
    return true;
  }
  return assessment.target_date != null;
};

const isPrimaryOrSecondarySponsor = (member: ExecutiveTeamMember): boolean => {
  const type = member.executive_type?.trim()?.toLowerCase();
  return type === "primary" || type === "secondary";
};

/** Max unique customer contacts mapped to any primary/secondary sponsor. */
const computeSponsorFanOut = (
  relationships: StakeholderRelationship[],
  executiveSponsors: ExecutiveTeamMember[]
): number | null => {
  const primarySecondaryIds = new Set(
    executiveSponsors
      .filter(isPrimaryOrSecondarySponsor)
      .map(member => member.user_id)
      .filter(Boolean)
  );
  if (primarySecondaryIds.size === 0) return null;

  const contactsBySponsor = new Map<string, Set<string>>();
  primarySecondaryIds.forEach(id => contactsBySponsor.set(id, new Set()));

  relationships.forEach(item => {
    const sponsorId = item.executive_team_member;
    const contactId = item.customer_team_member;
    if (!sponsorId || !contactId || !primarySecondaryIds.has(sponsorId)) {
      return;
    }
    contactsBySponsor.get(sponsorId)?.add(contactId);
  });

  let max = 0;
  contactsBySponsor.forEach(contacts => {
    max = Math.max(max, contacts.size);
  });
  return max;
};

/**
 * Max vendor assignments (exec + account team) mapped onto any single
 * customer contact.
 */
const computeContactFanIn = (
  relationships: StakeholderRelationship[]
): number => {
  const vendorsByContact = new Map<string, Set<string>>();

  relationships.forEach(item => {
    const contactId = item.customer_team_member;
    if (!contactId) return;

    const vendorId =
      item.executive_team_member || item.account_team_member || null;
    if (!vendorId) return;

    const vendors = vendorsByContact.get(contactId) ?? new Set<string>();
    vendors.add(`${item.team_type}:${vendorId}`);
    vendorsByContact.set(contactId, vendors);
  });

  if (vendorsByContact.size === 0) return 0;

  let max = 0;
  vendorsByContact.forEach(vendors => {
    max = Math.max(max, vendors.size);
  });
  return max;
};

const AlignmentCoverageBody: React.FC<{
  stats: CoverageStat[];
  loading: boolean;
}> = ({ stats, loading }) =>
  loading ? (
    <p className="m-0 text-sm text-gray-400">Loading coverage metrics…</p>
  ) : (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      {stats.map(stat => (
        <div
          key={stat.id}
          className="rounded-lg border border-gray-200 bg-white px-3 py-4 text-center"
          title={stat.tooltip}
        >
          <div
            className={`text-xl font-semibold tabular-nums leading-tight ${
              stat.valueClassName || "text-gray-900"
            }`}
          >
            {stat.value}
          </div>
          <div className="mt-1.5 text-xs font-medium text-gray-500">
            {stat.label}
          </div>
          {stat.subText ? (
            <div className="mt-0.5 text-[11px] text-gray-400">
              {stat.subText}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );

const AlignmentCoverageSection: React.FC<AlignmentCoverageSectionProps> = ({
  assessmentLevel,
  executiveSponsors,
  relationships,
  loading,
  onOpenRelationships,
  embedded = false,
}) => {
  const dispatch = useAppDispatch();
  const rlSettingValue = useAppSelector(
    state => state.vendorAdmin.rlSettingValue
  );

  useEffect(() => {
    if (!rlSettingValue) {
      dispatch(fetchRlSettingConfig());
    }
  }, [dispatch, rlSettingValue]);

  const fanInCap = normalizeFanInCap(rlSettingValue?.fan_in_cap);

  const stats = useMemo((): CoverageStat[] => {
    const active = relationships.filter(item => item.active !== false);
    const pairsMapped = active.length;

    const assessedRls = active
      .map(item => item.latest_assessment?.rl)
      .filter((rl): rl is number => rl != null && Number.isFinite(rl));
    const pairsAssessed = assessedRls.length;
    const unassessed = Math.max(0, pairsMapped - pairsAssessed);

    const pairsWithGoals = active.filter(item =>
      hasGoal(item.latest_assessment)
    ).length;
    const withoutGoals = Math.max(0, pairsMapped - pairsWithGoals);

    const sponsorFanOut = computeSponsorFanOut(active, executiveSponsors);
    const contactFanIn = computeContactFanIn(active);
    const fanInColorClass =
      contactFanIn > (fanInCap || DEFAULT_FAN_IN_CAP)
        ? "text-red-600"
        : "text-gray-900";

    const al = parseAlNumber(assessmentLevel);
    const rl =
      assessedRls.length > 0
        ? Math.round(
            assessedRls.reduce((sum, n) => sum + n, 0) / assessedRls.length
          )
        : null;

    const alRlValue = `${al != null ? `AL ${al}` : "AL ?"} | ${
      rl != null ? `RL ${rl}` : "RL ?"
    }`;
    const alRlTooltipBase =
      "AL (Account Level): Account-level score reflecting overall executive alignment. RL (Relationship Level): Average of all assessed pair-level relationship scores on this account.";

    return [
      {
        id: "al-rl",
        value: alRlValue,
        label: "Current AL & RL",
        tooltip:
          al == null || rl == null
            ? `${alRlTooltipBase} Unassessed.`
            : alRlTooltipBase,
      },
      {
        id: "mapped",
        value: String(pairsMapped),
        label: "Pairs Mapped",
        tooltip:
          "Total vendor↔customer contact pairs mapped on this account. Each pair represents a relationship between a vendor stakeholder and a customer contact.",
      },
      {
        id: "assessed",
        value: String(pairsAssessed),
        label: "Pairs Assessed",
        subText: `${unassessed} mapped unassessed`,
        tooltip: `Mapped pairs that have an RL assessment captured. ${unassessed} pair(s) are mapped but not yet assessed.`,
      },
      {
        id: "goals",
        value: String(pairsWithGoals),
        label: "Pairs with Goals",
        subText: `${withoutGoals} without`,
        tooltip: `Pairs that have a target RL score or target date set. ${withoutGoals} pair(s) do not have goals defined yet.`,
      },
      {
        id: "fan-out",
        value: sponsorFanOut != null ? String(sponsorFanOut) : "—",
        label: "Sponsor Fan-out",
        subText: "contacts / sponsor",
        tooltip:
          sponsorFanOut != null
            ? `Highest fan-out among primary/secondary sponsors on this account: ${sponsorFanOut} customer contact(s) mapped to the most loaded sponsor.`
            : "No primary or secondary sponsors assigned to this account.",
      },
      {
        id: "fan-in",
        value: `${contactFanIn}/${fanInCap || DEFAULT_FAN_IN_CAP}`,
        label: "Contact Fan-in",
        subText: "stakeholders / contact",
        valueClassName: fanInColorClass,
        tooltip: `Highest fan-in on this account: ${contactFanIn} vendor stakeholder(s) mapped to the most covered customer contact, out of ${fanInCap || DEFAULT_FAN_IN_CAP} capacity.`,
      },
    ];
  }, [assessmentLevel, executiveSponsors, fanInCap, relationships]);

  if (embedded) {
    return <AlignmentCoverageBody stats={stats} loading={loading} />;
  }

  return (
    <SynopsisSectionCard
      icon={Target}
      title="Alignment & Coverage KPI's"
      linkLabel="Relationships"
      onLinkClick={onOpenRelationships}
    >
      <AlignmentCoverageBody stats={stats} loading={loading} />
    </SynopsisSectionCard>
  );
};

export default AlignmentCoverageSection;
